import React, { useEffect, useMemo, useState } from 'react';
import { Text } from '@cuemath/web-comps';
import { useGetQueueTickets } from 'actions/support-queue';
import TicketRows, { EmptyPane, RowHeader } from './ticket-rows';
import { getRestAPIGetErrorMessage } from 'utils';
import { MODAL_KIND, PANES, QUEUE_VIEW } from '../constants';
import {
  CloseButton,
  Count,
  Message,
  ModalCard,
  ModalHead,
  PagerBar,
  PagerButton,
  PaneTitle,
  RowList,
  Scrim,
} from '../styled-comps';

const PAGE_SIZE = 25;

const MODAL_TITLES = {
  [MODAL_KIND.TOTAL]: 'All live tickets',
  [MODAL_KIND.RESOLVED]: 'Resolved in this window',
};

const BUCKET_TITLES = {
  by_agent: 'Resolved by an agent',
  by_bot: 'Resolved by the bot',
  abandoned: 'Abandoned at the bot',
};

/**
 * Drill-down for the tiles that have no pane of their own.
 *
 * Reads the dashboard's own list endpoint so the rows are the set the tile
 * counted. The agent inbox endpoint cannot serve it — its user_type and
 * source filters AND together so they cannot express the board's scope, and
 * it has no window filter, so a chip reading 10 opened a list of 64.
 */
const QueueModal = ({ kind, bucket, pane, query, showRegion, nowSeconds, onClose }) => {
  const paneConfig = PANES.find(p => p.key === pane);
  const [page, setPage] = useState(1);
  const {
    data,
    getAll,
    resource,
    isProcessing,
    isProcessingFailed,
  } = useGetQueueTickets();
  // NOT the hook's `error`: it reads `resource.error`, a key nothing writes.
  // The failure is filed under `__ERROR__`, which this helper reads. Left as
  // it was, a 403 rendered as "Nothing here" under a header reading 0 — a
  // supervisor told the queue is empty when the request was refused.
  const errorMessage = isProcessingFailed
    ? getRestAPIGetErrorMessage(resource) || 'the queue service did not respond'
    : null;

  // `query` carries the board's region and window — the same filters the
  // tile was counted under, so the list cannot drift from the number.
  const params = useMemo(
    () => ({
      ...query,
      view: kind === MODAL_KIND.RESOLVED ? QUEUE_VIEW.RESOLVED : QUEUE_VIEW.LIVE,
      ...(bucket ? { bucket } : {}),
      ...(pane ? { pane } : {}),
      page,
      limit: PAGE_SIZE,
    }),
    [kind, bucket, pane, page, query],
  );

  // A different bucket is a different list; keeping the old page number
  // would open it on page 4 of a 2-page result and read as empty.
  useEffect(() => {
    setPage(1);
  }, [kind, bucket, pane]);

  useEffect(() => {
    getAll(params);
  }, [getAll, params]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The hook drops `data` the moment a new page is requested, so paging
  // rendered an empty list for the length of the round trip — the card
  // collapsed to nothing, the count flashed 0, then it snapped back to full
  // height. Hold the page that is on screen until its replacement arrives.
  const [shown, setShown] = useState(null);

  useEffect(() => {
    if (data) setShown(data);
  }, [data]);

  useEffect(() => {
    setShown(null);
  }, [kind, bucket, pane]);

  const current = data || shown;
  const rows = Array.isArray(current?.tickets) ? current.tickets : [];
  const total = current?.total ?? rows.length;
  const hasNext = current?.has_next;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Bot and unassigned rows have no owner, exactly as in their panes.
  const showOwner = paneConfig ? paneConfig.showOwner : true;

  return (
    <Scrim onClick={onClose}>
      <ModalCard $paged={pageCount > 1} onClick={e => e.stopPropagation()}>
        <ModalHead>
          <PaneTitle>
            {(bucket && BUCKET_TITLES[bucket]) ||
              paneConfig?.title ||
              MODAL_TITLES[kind] ||
              'Tickets'}
          </PaneTitle>
          <Count>{total}</Count>
          <CloseButton type="button" onClick={onClose}>
            Close
          </CloseButton>
        </ModalHead>

        {errorMessage ? (
          <Message>
            <Text variant="body1">Could not load this list — {errorMessage}</Text>
          </Message>
        ) : (
          <>
            <RowHeader showRegion={showRegion} showOwner={showOwner} />
            <RowList $fill>
              {rows.length === 0 && !isProcessing ? (
                <EmptyPane
                  title="Nothing here"
                  note="No ticket in this window matches."
                />
              ) : (
                <TicketRows
                  rows={rows}
                  nowSeconds={nowSeconds}
                  showRegion={showRegion}
                  showOwner={showOwner}
                />
              )}
            </RowList>
            <PagerBar>
              <span>
                page {page} of {pageCount}
                {isProcessing ? ' · loading…' : ''}
              </span>
              <PagerButton
                type="button"
                disabled={page === 1 || isProcessing}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Prev
              </PagerButton>
              <PagerButton
                type="button"
                disabled={!hasNext || isProcessing}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </PagerButton>
            </PagerBar>
          </>
        )}
      </ModalCard>
    </Scrim>
  );
};

export default QueueModal;
